// Análise de qualidade de foto de documento, toda no navegador (sem enviar nada ao servidor).
// Heurísticas simples e rápidas: servem para orientar o cliente, nunca para bloquear o envio.

export interface QualityReport {
  brightness: number; // média de luminância 0–255
  glare: number; // fração de pixels estourados (0–1)
  sharpness: number; // variância do Laplaciano numa versão reduzida da imagem
  issues: QualityIssue[];
}

export type QualityIssue = 'dark' | 'bright' | 'glare' | 'blurry';

const ANALYSIS_WIDTH = 480;

// Limiares calibrados para documento com texto ocupando a maior parte do quadro
const DARK_BELOW = 75;
const BRIGHT_ABOVE = 215;
const GLARE_ABOVE = 0.035;
const BLURRY_BELOW = 55;

/** Analisa uma região de uma imagem/vídeo/canvas. A região é dada em pixels da fonte. */
export function analyzeRegion(
  source: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  scratch?: HTMLCanvasElement
): QualityReport {
  const canvas = scratch || document.createElement('canvas');
  const scale = Math.min(1, ANALYSIS_WIDTH / sw);
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { brightness: 128, glare: 0, sharpness: 999, issues: [] };
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  const gray = new Float32Array(w * h);
  let sum = 0;
  let blown = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    gray[p] = y;
    sum += y;
    if (y > 248) blown += 1;
  }
  const brightness = sum / gray.length;
  const glare = blown / gray.length;

  // Variância do Laplaciano (4-vizinhos): baixa = poucas bordas nítidas = foto tremida/desfocada
  let lapSum = 0;
  let lapSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      const lap = gray[i - w] + gray[i + w] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      lapSum += lap;
      lapSq += lap * lap;
      n += 1;
    }
  }
  const mean = n ? lapSum / n : 0;
  const sharpness = n ? lapSq / n - mean * mean : 0;

  const issues: QualityIssue[] = [];
  if (brightness < DARK_BELOW) issues.push('dark');
  else if (brightness > BRIGHT_ABOVE) issues.push('bright');
  if (glare > GLARE_ABOVE) issues.push('glare');
  if (sharpness < BLURRY_BELOW) issues.push('blurry');

  return { brightness, glare, sharpness, issues };
}

export function analyzeImage(img: HTMLImageElement | HTMLCanvasElement): QualityReport {
  const w = img instanceof HTMLImageElement ? img.naturalWidth : img.width;
  const h = img instanceof HTMLImageElement ? img.naturalHeight : img.height;
  return analyzeRegion(img, 0, 0, w, h);
}

export function loadImageFromFile(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

export function canUseLiveCamera(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
}
